import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";

const API = "https://danielcantos.net/api";

const tierLabels = ["1 FCL", "2 FCL", "3 FCL", "4 FCL", "5 FCL"];
const monthLabels = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sept","Oct","Nov","Dic"];

const BG = "#f1f5f9";
const CARD = "#ffffff";
const TEAL = "#3b82f6";
const MAGENTA = "#93c5fd";
const AMBER = "#f59e0b";
const TEXT = "#0f172a";
const TEXT_DIM = "#64748b";
const BORDER = "#e2e8f0";

function fmt(n) {
  return Math.round(n).toLocaleString("es-EC");
}
function fmtPct(n) {
  return (n * 100).toLocaleString("es-EC", { maximumFractionDigits: 1 }) + "%";
}

const fieldStyle = { width:90, padding:"6px 8px", border:`1px solid ${BORDER}`, borderRadius:6, fontSize:13, background:"#f8fafc", color:TEXT };
const labelStyle = { fontSize:11, color:TEXT_DIM, fontWeight:600, display:"block", marginBottom:4 };
const cardStyle = { background:CARD, border:`1px solid ${BORDER}`, borderRadius:10, padding:"16px 20px", marginBottom:16 };

/* ---------- VISTA ANUAL (por escalón de contenedores) ---------- */
function calcRowsAnual(params) {
  return tierLabels.map((label, i) => {
    const ali = params.aliPct[i] / 100;
    const coast = 1 - ali;
    const container = params.baseContainer + params.increment * i;
    const inversionAli = container * params.priceMult;
    const valoracionCoast = params.valorCoast;
    const total = inversionAli + valoracionCoast;
    const pctAliInv = total ? inversionAli / total : 0;
    const pctCoastInv = total ? valoracionCoast / total : 0;
    const margen = params.margen / 100;
    const capAli = inversionAli * margen * ali;
    const capCoast = inversionAli * margen * coast;
    const months = params.months[i];
    const accumCapAli = capAli * months;
    const accumCapCoast = capCoast * months;
    const utilidadTotal = accumCapAli + accumCapCoast;
    const aliTotal = inversionAli + accumCapCoast;
    const coastTotal = valoracionCoast + accumCapAli;
    const grandTotal = aliTotal + coastTotal;
    const pctAli2 = grandTotal ? aliTotal / grandTotal : 0;
    const pctCoast2 = grandTotal ? coastTotal / grandTotal : 0;
    const coastAAli = coast ? ((-ali * aliTotal - coastTotal * ali + aliTotal) / coast) : 0;
    const ajustAli = aliTotal - coastAAli;
    const ajustDenom = ajustAli + coastTotal;
    const finalAli = ajustDenom ? ajustAli / ajustDenom : 0;
    const finalCoast = 1 - finalAli;
    return { label, ali, coast, container, inversionAli, valoracionCoast, total, pctAliInv, pctCoastInv,
      capAli, capCoast, accumCapAli, accumCapCoast, utilidadTotal, aliTotal, coastTotal, grandTotal,
      pctAli2, pctCoast2, coastAAli, ajustAli, finalAli, finalCoast };
  });
}

function VistaAnualParams({ params, update, updateTier }) {
  return (
    <>
      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Parámetros generales</h3>
        <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
          <div><label style={labelStyle}>Valorización Coast (USD)</label><input type="number" style={{...fieldStyle,width:110}} value={params.valorCoast} onChange={e=>update("valorCoast", parseFloat(e.target.value)||0)}/></div>
          <div><label style={labelStyle}>Margen (%)</label><input type="number" style={fieldStyle} value={params.margen} onChange={e=>update("margen", parseFloat(e.target.value)||0)}/></div>
          <div><label style={labelStyle}>Valor base 1er FCL (USD)</label><input type="number" style={{...fieldStyle,width:110}} value={params.baseContainer} onChange={e=>update("baseContainer", parseFloat(e.target.value)||0)}/></div>
          <div><label style={labelStyle}>Incremento por FCL (USD)</label><input type="number" style={{...fieldStyle,width:110}} value={params.increment} onChange={e=>update("increment", parseFloat(e.target.value)||0)}/></div>
          <div><label style={labelStyle}>Multiplicador precio (x)</label><input type="number" style={fieldStyle} value={params.priceMult} onChange={e=>update("priceMult", parseFloat(e.target.value)||0)}/></div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Participación (%) de Ali por escalón — Coast se calcula automático</h3>
        <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
          {tierLabels.map((label, i) => (
            <div key={i}><label style={labelStyle}>{label} — Ali %</label><input type="number" style={fieldStyle} value={params.aliPct[i]} onChange={e=>updateTier("aliPct", i, parseFloat(e.target.value)||0)}/></div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Meses de acumulación por escalón</h3>
        <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
          {tierLabels.map((label, i) => (
            <div key={i}><label style={labelStyle}>{label}</label><input type="number" style={fieldStyle} value={params.months[i]} onChange={e=>updateTier("months", i, parseFloat(e.target.value)||0)}/></div>
          ))}
        </div>
      </div>
    </>
  );
}

function VistaAnualBody({ params }) {
  const rows = calcRowsAnual(params);

  const totalMonths = params.months.reduce((a,b) => a+b, 0);
  const utilidadAnual = rows.reduce((a,r) => a+r.utilidadTotal, 0);
  const coastAAliAnual = rows.reduce((a,r) => a+r.coastAAli, 0);
  const finalAliPonderado = totalMonths ? rows.reduce((a,r,i) => a + r.finalAli*params.months[i], 0) / totalMonths : 0;
  const finalCoastPonderado = 1 - finalAliPonderado;

  const chartParticipacion = rows.map(r => ({ escalon: r.label, Ali: Math.round(r.finalAli*1000)/10, Coast: Math.round(r.finalCoast*1000)/10 }));
  const chartUtilidad = rows.map(r => ({ escalon: r.label, Utilidad: Math.round(r.utilidadTotal), CoastAAli: Math.round(r.coastAAli) }));

  const sections = [
    { title: "Participación e inversión", items: [
      ["Ali %", r => fmtPct(r.ali)],
      ["Coast %", r => fmtPct(r.coast)],
      ["Valor contenedor(es)", r => "$" + fmt(r.container)],
      ["Inversión Ali", r => "$" + fmt(r.inversionAli)],
      ["Valorización Coast", r => "$" + fmt(r.valoracionCoast)],
      ["Total", r => "$" + fmt(r.total)],
      ["% Ali (por inversión)", r => fmtPct(r.pctAliInv)],
      ["% Coast (por inversión)", r => fmtPct(r.pctCoastInv)],
    ]},
    { title: "Capitalización mensual", items: [
      ["Capitalización Ali/mes", r => "$" + fmt(r.capAli)],
      ["Capitalización Coast/mes", r => "$" + fmt(r.capCoast)],
      ["Acumulado Ali (periodo)", r => "$" + fmt(r.accumCapAli)],
      ["Acumulado Coast (periodo)", r => "$" + fmt(r.accumCapCoast)],
      ["Utilidad total generada", r => "$" + fmt(r.utilidadTotal)],
    ]},
    { title: "Totales cruzados", items: [
      ["Total Ali", r => "$" + fmt(r.aliTotal)],
      ["Total Coast", r => "$" + fmt(r.coastTotal)],
      ["Gran total", r => "$" + fmt(r.grandTotal)],
      ["% Ali (total)", r => fmtPct(r.pctAli2)],
      ["% Coast (total)", r => fmtPct(r.pctCoast2)],
    ]},
    { title: "Ajuste final", items: [
      ["Coast a Ali", r => "$" + fmt(r.coastAAli)],
      ["Ali ajustado", r => "$" + fmt(r.ajustAli)],
      ["% Ali final", r => fmtPct(r.finalAli)],
      ["% Coast final", r => fmtPct(r.finalCoast)],
    ]},
  ];

  return (
    <>
      <div style={{...cardStyle, padding:"14px 16px", border:`1px solid ${totalMonths===12?TEAL:AMBER}`}}>
        <h3 style={{fontSize:13, margin:"0 0 4px", color:TEXT_DIM, fontWeight:600}}>Total anual ponderado</h3>
        <div style={{fontSize:11, color: totalMonths===12 ? TEAL : AMBER, marginBottom:12}}>
          {totalMonths===12 ? "Los 5 escalones suman 12 meses ✓" : `Los 5 escalones suman ${totalMonths} meses (ajusta para que sumen 12 y representen el año completo)`}
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:14}}>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Utilidad total del año</div>
            <div style={{fontSize:20, fontWeight:700, color:TEAL}}>${fmt(utilidadAnual)}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Ajuste Coast a Ali (año)</div>
            <div style={{fontSize:20, fontWeight:700, color: coastAAliAnual >= 0 ? TEAL : "#dc2626"}}>${fmt(coastAAliAnual)}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Participación final ponderada</div>
            <div style={{fontSize:16, fontWeight:700, color:AMBER}}>Ali {fmtPct(finalAliPonderado)} · Coast {fmtPct(finalCoastPonderado)}</div>
          </div>
        </div>
      </div>

      <div style={{...cardStyle, padding:"14px 16px"}}>
        <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Resumen por escalón</h3>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:10}}>
          {rows.map((r,i) => (
            <div key={i} style={{background:"#f8fafc", border:`1px solid ${BORDER}`, borderRadius:8, padding:"10px 12px"}}>
              <div style={{fontWeight:700, color:MAGENTA, fontSize:13, marginBottom:6}}>{r.label}</div>
              <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Utilidad total generada</div>
              <div style={{fontSize:15, fontWeight:700, color:TEAL, marginBottom:6}}>${fmt(r.utilidadTotal)}</div>
              <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Ajuste Coast a Ali</div>
              <div style={{fontSize:14, fontWeight:600, color: r.coastAAli >= 0 ? TEAL : "#dc2626", marginBottom:6}}>${fmt(r.coastAAli)}</div>
              <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Participación final</div>
              <div style={{fontSize:12.5, color:AMBER}}>Ali {fmtPct(r.finalAli)} · Coast {fmtPct(r.finalCoast)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))", gap:16, marginBottom:16}}>
        <div style={cardStyle}>
          <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Participación final por escalón (%)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartParticipacion}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="escalon" stroke={TEXT_DIM} fontSize={12} />
              <YAxis stroke={TEXT_DIM} fontSize={12} unit="%" />
              <Tooltip contentStyle={{background:CARD, border:`1px solid ${BORDER}`, borderRadius:8}} labelStyle={{color:TEXT}} />
              <Legend wrapperStyle={{fontSize:12, color:TEXT_DIM}} />
              <Bar dataKey="Ali" fill={TEAL} radius={[4,4,0,0]} />
              <Bar dataKey="Coast" fill={MAGENTA} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={cardStyle}>
          <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Utilidad y ajuste Coast a Ali por escalón (USD)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartUtilidad}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="escalon" stroke={TEXT_DIM} fontSize={12} />
              <YAxis stroke={TEXT_DIM} fontSize={12} />
              <Tooltip contentStyle={{background:CARD, border:`1px solid ${BORDER}`, borderRadius:8}} labelStyle={{color:TEXT}} formatter={(v)=>"$"+fmt(v)} />
              <Legend wrapperStyle={{fontSize:12, color:TEXT_DIM}} />
              <Line type="monotone" dataKey="Utilidad" stroke={TEAL} strokeWidth={2} dot={{fill:TEAL}} />
              <Line type="monotone" dataKey="CoastAAli" stroke={AMBER} strokeWidth={2} dot={{fill:AMBER}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Resultado completo por escalón</h3>
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse", width:"100%", fontSize:12.5}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                <th style={{padding:"6px 8px", textAlign:"left", color:MAGENTA, fontSize:11.5, borderBottom:`1px solid ${BORDER}`}}>Concepto</th>
                {tierLabels.map(l => <th key={l} style={{padding:"6px 8px", textAlign:"right", color:MAGENTA, fontSize:11.5, borderBottom:`1px solid ${BORDER}`}}>{l}</th>)}
              </tr>
            </thead>
            <tbody>
              {sections.map(sec => (
                <>
                  <tr key={sec.title}>
                    <td colSpan={6} style={{padding:"8px", fontWeight:700, color:TEAL, borderBottom:`2px solid ${BORDER}`}}>{sec.title}</td>
                  </tr>
                  {sec.items.map(([label, fn]) => (
                    <tr key={label}>
                      <td style={{padding:"6px 8px", borderBottom:`1px solid ${BORDER}`, color:TEXT_DIM}}>{label}</td>
                      {rows.map((r,i) => <td key={i} style={{padding:"6px 8px", textAlign:"right", borderBottom:`1px solid ${BORDER}`, whiteSpace:"nowrap", color:TEXT}}>{fn(r)}</td>)}
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{fontSize:11, color:TEXT_DIM, marginTop:8}}>"Coast a Ali" es el ajuste final que rebalancea el reparto entre inversión directa y capitalización acumulada.</div>
      </div>
    </>
  );
}

/* ---------- VISTA MENSUAL ---------- */
function calcRowsMensual(mp) {
  const rows = monthLabels.map((label, i) => {
    const ali = mp.aliPct[i] / 100;
    const coast = 1 - ali;
    const inversionAli = mp.containerValue[i] * mp.priceMult;
    const valoracionCoast = mp.valorCoast;
    const margen = mp.margen / 100;
    const capAliBruta = inversionAli * margen * ali;
    const capCoastBruta = inversionAli * margen * coast;
    const retiroAli = mp.retiroAli[i] || 0;
    const retiroCoast = mp.retiroCoast[i] || 0;
    const capAli = capAliBruta - retiroAli;
    const capCoast = capCoastBruta - retiroCoast;
    return { label, ali, coast, inversionAli, valoracionCoast, capAliBruta, capCoastBruta, retiroAli, retiroCoast, capAli, capCoast };
  });

  let accAli = 0, accCoast = 0;
  return rows.map((r) => {
    accAli += r.capAli;
    accCoast += r.capCoast;
    const aliCum = r.inversionAli + accAli;
    const coastCum = r.valoracionCoast + accCoast;
    const totalCum = aliCum + coastCum;
    const pctAliReal = totalCum ? aliCum / totalCum : 0;
    const pctCoastReal = 1 - pctAliReal;
    // Break Even Coast-Ali: monto que hay que transferir para que la
    // participacion real quede exactamente igual al % objetivo (Ali) de ese mes.
    // Positivo = Coast le paga a Ali. Negativo = Ali le paga a Coast.
    const breakEven = aliCum - totalCum * r.ali;
    const coastAAli = -breakEven;
    return { ...r, aliCum, coastCum, totalCum, pctAliReal, pctCoastReal, coastAAli };
  });
}

function VistaMensualParams({ mp, updateM, updateMonth }) {
  return (
    <>
      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Parámetros generales</h3>
        <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
          <div><label style={labelStyle}>Valorización Coast (USD)</label><input type="number" style={{...fieldStyle,width:110}} value={mp.valorCoast} onChange={e=>updateM("valorCoast", parseFloat(e.target.value)||0)}/></div>
          <div><label style={labelStyle}>Margen (%)</label><input type="number" style={fieldStyle} value={mp.margen} onChange={e=>updateM("margen", parseFloat(e.target.value)||0)}/></div>
          <div><label style={labelStyle}>Multiplicador precio (x)</label><input type="number" style={fieldStyle} value={mp.priceMult} onChange={e=>updateM("priceMult", parseFloat(e.target.value)||0)}/></div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>% Ali por mes — Coast se calcula automático</h3>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          {monthLabels.map((label, i) => (
            <div key={i}><label style={labelStyle}>{label}</label><input type="number" style={{...fieldStyle,width:70}} value={mp.aliPct[i]} onChange={e=>updateMonth("aliPct", i, parseFloat(e.target.value)||0)}/></div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Valor del contenedor por mes (USD)</h3>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          {monthLabels.map((label, i) => (
            <div key={i}>
              <label style={labelStyle}>{label}</label>
              <input type="number" style={{...fieldStyle,width:80}} value={mp.containerValue[i]} onChange={e=>updateMonth("containerValue", i, parseFloat(e.target.value)||0)}/>
              <div style={{fontSize:10, color:TEAL, marginTop:3, textAlign:"center"}}>{(mp.containerValue[i]/45000).toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2})} FCL</div>
            </div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Retiro de utilidad Ali por mes (USD)</h3>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          {monthLabels.map((label, i) => (
            <div key={i}><label style={labelStyle}>{label}</label><input type="number" style={{...fieldStyle,width:80}} value={mp.retiroAli[i]} onChange={e=>updateMonth("retiroAli", i, parseFloat(e.target.value)||0)}/></div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Retiro de utilidad Coast por mes (USD)</h3>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          {monthLabels.map((label, i) => (
            <div key={i}><label style={labelStyle}>{label}</label><input type="number" style={{...fieldStyle,width:80}} value={mp.retiroCoast[i]} onChange={e=>updateMonth("retiroCoast", i, parseFloat(e.target.value)||0)}/></div>
          ))}
        </div>
      </div>
    </>
  );
}

function VistaMensualBody({ mp }) {
  const acc = calcRowsMensual(mp);
  const utilidadAliAnual = acc.reduce((a,r) => a+r.capAli, 0);
  const utilidadCoastAnual = acc.reduce((a,r) => a+r.capCoast, 0);
  const diciembre = acc[11];

  const chartPct = acc.map((r) => ({ mes: r.label, "Ali objetivo": Math.round(r.ali*1000)/10, "Ali real acumulado": Math.round(r.pctAliReal*1000)/10 }));
  const chartUtil = acc.map(r => ({ mes: r.label, "Utilidad Ali": Math.round(r.capAli), "Utilidad Coast": Math.round(r.capCoast) }));
  const chartAjuste = acc.map(r => ({ mes: r.label, "Coast a Ali": Math.round(r.coastAAli) }));

  return (
    <>
      <div style={{...cardStyle, padding:"14px 16px", border:`1px solid ${TEAL}`}}>
        <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Final del periodo</h3>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:14}}>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>{diciembre.coastAAli >= 0 ? "Coast debe pagar a Ali" : "Ali debe pagar a Coast"}</div>
            <div style={{fontSize:22, fontWeight:700, color: diciembre.coastAAli >= 0 ? TEAL : AMBER}}>${fmt(Math.abs(diciembre.coastAAli))}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Ali acumulado a diciembre</div>
            <div style={{fontSize:18, fontWeight:700, color:TEAL}}>${fmt(diciembre.aliCum)}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Coast acumulado a diciembre</div>
            <div style={{fontSize:18, fontWeight:700, color:MAGENTA}}>${fmt(diciembre.coastCum)}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Utilidad Ali (año)</div>
            <div style={{fontSize:16, fontWeight:700, color:TEAL}}>${fmt(utilidadAliAnual)}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Utilidad Coast (año)</div>
            <div style={{fontSize:16, fontWeight:700, color:MAGENTA}}>${fmt(utilidadCoastAnual)}</div>
          </div>
          <div>
            <div style={{fontSize:11, color:TEXT_DIM, marginBottom:2}}>Participación real a diciembre</div>
            <div style={{fontSize:16, fontWeight:700, color:AMBER}}>Ali {fmtPct(diciembre.pctAliReal)} · Coast {fmtPct(diciembre.pctCoastReal)}</div>
          </div>
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))", gap:16, marginBottom:16}}>
        <div style={cardStyle}>
          <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>% Ali objetivo vs % Ali real acumulado</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartPct}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="mes" stroke={TEXT_DIM} fontSize={11} />
              <YAxis stroke={TEXT_DIM} fontSize={11} unit="%" />
              <Tooltip contentStyle={{background:CARD, border:`1px solid ${BORDER}`, borderRadius:8}} labelStyle={{color:TEXT}} />
              <Legend wrapperStyle={{fontSize:11, color:TEXT_DIM}} />
              <Line type="monotone" dataKey="Ali objetivo" stroke={AMBER} strokeWidth={2} dot={{fill:AMBER, r:3}} />
              <Line type="monotone" dataKey="Ali real acumulado" stroke={TEAL} strokeWidth={2} dot={{fill:TEAL, r:3}} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={cardStyle}>
          <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Utilidad mensual Ali vs Coast (USD)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartUtil}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="mes" stroke={TEXT_DIM} fontSize={11} />
              <YAxis stroke={TEXT_DIM} fontSize={11} />
              <Tooltip contentStyle={{background:CARD, border:`1px solid ${BORDER}`, borderRadius:8}} labelStyle={{color:TEXT}} formatter={(v)=>"$"+fmt(v)} />
              <Legend wrapperStyle={{fontSize:11, color:TEXT_DIM}} />
              <Bar dataKey="Utilidad Ali" fill={TEAL} radius={[3,3,0,0]} />
              <Bar dataKey="Utilidad Coast" fill={MAGENTA} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:13, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Ajuste "Coast a Ali" acumulado, mes a mes (USD)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartAjuste}>
            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
            <XAxis dataKey="mes" stroke={TEXT_DIM} fontSize={11} />
            <YAxis stroke={TEXT_DIM} fontSize={11} />
            <Tooltip contentStyle={{background:CARD, border:`1px solid ${BORDER}`, borderRadius:8}} labelStyle={{color:TEXT}} formatter={(v)=>"$"+fmt(v)} />
            <Line type="monotone" dataKey="Coast a Ali" stroke={AMBER} strokeWidth={2} dot={{fill:AMBER, r:3}} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={cardStyle}>
        <h3 style={{fontSize:14, margin:"0 0 12px", color:TEXT_DIM, fontWeight:600}}>Detalle mes a mes</h3>
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse", width:"100%", fontSize:12}}>
            <thead>
              <tr style={{background:"#f8fafc"}}>
                <th style={{padding:"5px 6px", textAlign:"left", color:MAGENTA, fontSize:11, borderBottom:`1px solid ${BORDER}`}}>Concepto</th>
                {monthLabels.map(l => <th key={l} style={{padding:"5px 6px", textAlign:"right", color:MAGENTA, fontSize:11, borderBottom:`1px solid ${BORDER}`}}>{l}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                ["Ali %", r => fmtPct(r.ali)],
                ["Coast %", r => fmtPct(r.coast)],
                ["Inversión Ali (mes)", r => "$"+fmt(r.inversionAli)],
                ["Valorización Coast (mes)", r => "$"+fmt(r.valoracionCoast)],
                ["Utilidad Ali (mes)", r => "$"+fmt(r.capAli)],
                ["Utilidad Coast (mes)", r => "$"+fmt(r.capCoast)],
                ["Inversión Ali acumulada", r => "$"+fmt(r.aliCum)],
                ["Inversión Coast acumulada", r => "$"+fmt(r.coastCum)],
                ["% Ali real acumulado", r => fmtPct(r.pctAliReal)],
                ["% Coast real acumulado", r => fmtPct(r.pctCoastReal)],
                ["Coast a Ali (corte)", r => "$"+fmt(r.coastAAli)],
              ].map(([label, fn]) => (
                <tr key={label}>
                  <td style={{padding:"5px 6px", borderBottom:`1px solid ${BORDER}`, color:TEXT_DIM}}>{label}</td>
                  {acc.map((r,i) => <td key={i} style={{padding:"5px 6px", textAlign:"right", borderBottom:`1px solid ${BORDER}`, whiteSpace:"nowrap", color:TEXT}}>{fn(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ---------- COMPONENTE PRINCIPAL ---------- */
export default function AlimareNY() {
  const [subview, setSubview] = useState("anual");
  const [params, setParams] = useState(null);
  const [monthlyParams, setMonthlyParams] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/alimare-ny/params`).then(res => setParams(res.data)).catch(() => {
      setParams({ valorCoast: 180000, margen: 6.5, baseContainer: 45000, increment: 45000, priceMult: 4.5, aliPct: [55,55,65,68,68], months: [3,3,6,6,6] });
    });
    axios.get(`${API}/alimare-ny/monthly-params`).then(res => setMonthlyParams(res.data)).catch(() => {
      setMonthlyParams({
        valorCoast: 180000, margen: 6.5, priceMult: 4.5,
        aliPct: [55,60,60,60,65,65,70,70,70,70,70,70],
        containerValue: [45000,90000,90000,90000,90000,112000,112000,112000,135000,157000,157000,157000],
        retiroAli: [0,0,0,10000,10000,10000,10000,10000,10000,10000,10000,10000],
        retiroCoast: [0,0,0,5000,5000,5000,5000,5000,5000,5000,5000,5000],
      });
    });
  }, []);

  const update = (field, value) => setParams(p => ({ ...p, [field]: value }));
  const updateTier = (field, idx, value) => setParams(p => {
    const arr = [...p[field]]; arr[idx] = value; return { ...p, [field]: arr };
  });
  const updateM = (field, value) => setMonthlyParams(p => ({ ...p, [field]: value }));
  const updateMonth = (field, idx, value) => setMonthlyParams(p => {
    const arr = [...p[field]]; arr[idx] = value; return { ...p, [field]: arr };
  });

  const save = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      if (subview === "anual") {
        await axios.post(`${API}/alimare-ny/params`, params);
      } else {
        await axios.post(`${API}/alimare-ny/monthly-params`, monthlyParams);
      }
      setSaveMsg("Guardado");
    } catch {
      setSaveMsg("Error al guardar");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 2500);
    }
  };

  if (!params || !monthlyParams) {
    return <div style={{padding:40,color:TEXT_DIM,background:BG,height:"100vh"}}>Cargando...</div>;
  }

  return (
    <div style={{overflowY:"auto", height:"calc(100vh - 60px)", background:BG, fontFamily:"sans-serif", color:TEXT, padding:"16px 20px 24px"}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14}}>
        <div style={{display:"flex", alignItems:"baseline", gap:10}}>
          <h2 style={{margin:0, color:TEXT, fontSize:16, fontWeight:600}}>Proyecto Alimare NY</h2>
          <span style={{fontSize:12, color:TEXT_DIM}}>Simulador de participación Ali / Coast</span>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <div style={{display:"flex", gap:4}}>
            <button onClick={()=>setSubview("anual")} style={{padding:"5px 12px", borderRadius:6, border:`1px solid ${BORDER}`, cursor:"pointer", fontWeight:500, fontSize:12, background: subview==="anual"?TEAL:"white", color: subview==="anual"?"white":TEXT_DIM}}>Anual</button>
            <button onClick={()=>setSubview("mensual")} style={{padding:"5px 12px", borderRadius:6, border:`1px solid ${BORDER}`, cursor:"pointer", fontWeight:500, fontSize:12, background: subview==="mensual"?TEAL:"white", color: subview==="mensual"?"white":TEXT_DIM}}>Mensual</button>
          </div>
          <button onClick={save} disabled={saving} style={{padding:"7px 14px", background:TEAL, color:"white", border:"none", borderRadius:6, cursor:"pointer", fontWeight:500, fontSize:12}}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>

      {saveMsg && <div style={{marginBottom:10, fontSize:12, color: saveMsg.includes("Error") ? "#dc2626" : TEAL}}>{saveMsg}</div>}

      {subview === "anual" ? (
        <>
          <VistaAnualParams params={params} update={update} updateTier={updateTier} />
          <VistaAnualBody params={params} />
        </>
      ) : (
        <>
          <VistaMensualParams mp={monthlyParams} updateM={updateM} updateMonth={updateMonth} />
          <VistaMensualBody mp={monthlyParams} />
        </>
      )}
    </div>
  );
}
